import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useQuery } from 'urql';
import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { QUERY } from './queries';
import { scrollToReviews } from '../../apps/product/utils';
import { sorts, submitReviewUrl } from './constants';
import CollectionFilters from '../CollectionFilters';
import css from './Reviews.module.scss';
import ErrorBoundary from '../ErrorBoundary';
import Overlay from '../Overlay';
import Paginator from '../Paginator';
import PhotoModal from './PhotoModal';
import ProductFitRatingReview from '../QuestionReviewsHeader/Review';
import Rating from '../Rating';
import ResultsListEmpty from '../ResultsListEmpty';
import Review from './Review';
import Loading from './Review/Loading';
import toGlobalID from '../../utils/toGlobalID';
import updateArray from '../../utils/array/update';
import SVGIcons from './SVGIcons';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var ratingOption = function ratingOption(ratingVal) {
  return {
    ariaLabel: "Filter by ".concat(ratingVal, " star rating"),
    labelString: "".concat(ratingVal, " star rating"),
    label: /*#__PURE__*/_jsx(Rating, {
      count: 5,
      size: 16,
      value: ratingVal
    }),
    value: "".concat(ratingVal)
  };
};

var NoResultsMessage = function NoResultsMessage(_ref) {
  var shopifyId = _ref.shopifyId;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      children: "No Reviews Yet. Be the first to\xA0"
    }), /*#__PURE__*/_jsx("a", {
      className: css.reviewsLink,
      href: submitReviewUrl(shopifyId),
      children: "write a review."
    })]
  });
};

NoResultsMessage.propTypes = {
  shopifyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
};

var getSizes = function getSizes(colorways) {
  var _colorways$edges;

  if ((colorways === null || colorways === void 0 ? void 0 : (_colorways$edges = colorways.edges) === null || _colorways$edges === void 0 ? void 0 : _colorways$edges.length) > 0) {
    var colorwayNode = colorways.edges[0].node;
    var variantStacks = colorwayNode.variantStacks;
    return variantStacks.map(function (_ref2) {
      var label = _ref2.size;
      return {
        label: label,
        value: label
      };
    });
  }

  return [];
};

var Reviews = function Reviews(_ref3) {
  var colorways = _ref3.colorways,
      filterByPhotos = _ref3.filterByPhotos,
      fitRatingAvg = _ref3.fitRatingAvg,
      queryLimit = _ref3.queryLimit,
      ratingAvg = _ref3.ratingAvg,
      reviewCount = _ref3.reviewCount,
      reviewWithPhotoCount = _ref3.reviewWithPhotoCount,
      shopifyId = _ref3.shopifyId,
      title = _ref3.title,
      viewAllLink = _ref3.viewAllLink;
  var sizes = getSizes(colorways);
  var defaultFilterBy = [{
    label: 'Rating',
    options: [ratingOption(5), ratingOption(4), ratingOption(3), ratingOption(2), ratingOption(1)],
    rating: true,
    selected: false
  }];

  if (sizes.length > 1) {
    defaultFilterBy.push({
      label: 'Size Purchased',
      options: sizes,
      size: true,
      selected: false
    });
  }

  var photoOptions = [{
    label: 'All Reviews',
    value: 'false'
  }, {
    label: 'Reviews with Photos',
    value: 'true'
  }];

  if (reviewWithPhotoCount > 0) {
    defaultFilterBy.push({
      label: 'All Reviews',
      options: photoOptions,
      selected: filterByPhotos ? photoOptions[1] : photoOptions[0],
      withPhoto: true
    });
  }

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      after = _useState2[0],
      setAfter = _useState2[1];

  var _useState3 = useState({
    by: defaultFilterBy,
    rating: null,
    size: null,
    withPhoto: filterByPhotos
  }),
      _useState4 = _slicedToArray(_useState3, 2),
      filters = _useState4[0],
      setFilters = _useState4[1];

  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      currentReview = _useState6[0],
      setCurrentReview = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      overlay = _useState8[0],
      setOverlay = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      overlayShowPhotos = _useState10[0],
      setOverlayShowPhotos = _useState10[1];

  var _useState11 = useState(sorts[0]),
      _useState12 = _slicedToArray(_useState11, 2),
      sort = _useState12[0],
      setSort = _useState12[1];

  var _useState13 = useState(0),
      _useState14 = _slicedToArray(_useState13, 2),
      pageIndex = _useState14[0],
      setPageIndex = _useState14[1];

  var scrollY = useRef(0);
  var variables = {
    after: after,
    first: queryLimit,
    orderBy: sort.orderBy,
    productShopifyId: shopifyId,
    rating: filters.rating,
    size: filters.size,
    withPhoto: filters.withPhoto
  };

  var _useQuery = useQuery({
    query: QUERY,
    variables: variables
  }),
      _useQuery2 = _slicedToArray(_useQuery, 1),
      _useQuery2$ = _useQuery2[0],
      data = _useQuery2$.data,
      error = _useQuery2$.error,
      fetching = _useQuery2$.fetching; // Help prevent RecommendationsAI component from loading too soon.


  if (!data && fetching) {
    return /*#__PURE__*/_jsx("section", {
      className: css.root,
      "aria-busy": "true",
      children: /*#__PURE__*/_jsx("ul", {
        className: css.list,
        children: Array.from(Array(4)).map(function (_, idx) {
          return /*#__PURE__*/_jsx("li", {
            className: css.item,
            children: /*#__PURE__*/_jsx(Loading, {})
          }, idx);
        })
      })
    });
  }

  if (!data) {
    return false;
  }

  var reviewData = data.reviews;
  var reviews = reviewData.edges.map(function (_ref4) {
    var review = _ref4.node;
    return _objectSpread({}, review);
  });
  var dataReviewCount = reviewData.totalCount;
  var results = reviews && reviews.length > 0;
  var isFiltered = !!filters.rating || !!filters.size || !!filters.withPhoto;
  var lastPageIndex = Math.ceil(dataReviewCount / queryLimit) - 1;

  var goToIndex = function goToIndex(newIndex) {
    var validIndex = newIndex > 0 && newIndex <= lastPageIndex;
    var nextAfter = false;
    var nextPageIndex = newIndex;

    if (validIndex) {
      nextAfter = btoa("arrayconnection:".concat(nextPageIndex * queryLimit - 1));
    }

    setAfter(nextAfter);
    setPageIndex(nextPageIndex);
    scrollToReviews();
  };

  var onFilterSelect = function onFilterSelect(filter, _ref5) {
    var value = _ref5.value;
    var newRating = filter.rating ? value : filters.rating;
    var newSize = filter.size ? value : filters.size;
    var newWithPhoto = filter.withPhoto ? value : filters.withPhoto; // By default we have withPhoto filter set to the first option

    if (filter.withPhoto && value === photoOptions[0].value && filters.withPhoto === null) {
      return;
    }

    var shouldReset = filter.selected && filter.selected.value === value;
    var newFilters = updateArray(filters.by, filters.by.indexOf(filter), {
      selected: filter.selected && filter.selected.value === value ? false : {
        label: value,
        value: value
      }
    });

    if (filter.size) {
      var options = filter.options;
      var option = options.find(function (_ref6) {
        var sizeValue = _ref6.value;
        return sizeValue === value;
      });
      newSize = option.label;
    }

    if (shouldReset) {
      newRating = null;
      newSize = null;
      newWithPhoto = null;
    }

    setFilters({
      by: newFilters,
      rating: newRating,
      size: newSize,
      withPhoto: newWithPhoto === 'true' ? newWithPhoto : null
    });
    goToIndex(0);
  };

  var onSortSelect = function onSortSelect(_ref7) {
    var value = _ref7.value;
    var newSort = sorts.find(function (srt) {
      return srt.value === value;
    }) || sorts[0];
    setSort(newSort);
    goToIndex(0);
  };

  var onClearAll = function onClearAll() {
    var newFilters = filters.by.reduce(function (arr, filter) {
      return arr.concat(_objectSpread(_objectSpread({}, filter), {}, {
        selected: filter.withPhoto ? photoOptions[0] : false
      }));
    }, []);
    setFilters({
      by: newFilters,
      rating: null,
      size: null,
      withPhoto: null
    });
    setSort(sorts[0]);
    goToIndex(0);
  };

  var _onClickImage = function onClickImage(review) {
    var scroller = document.querySelector('#all-photos');
    scrollY.current = !scroller ? 0 : scroller.firstChild.scrollTop;
    setOverlay(true);
    setOverlayShowPhotos(false);
    setCurrentReview(review);
  };

  var closeOverlay = function closeOverlay() {
    scrollY.current = 0;
    setOverlay(false);
  };

  var onSetReview = function onSetReview(review) {
    var scroller = document.querySelector('#all-photos');
    scrollY.current = !scroller ? 0 : scroller.firstChild.scrollTop;
    setOverlayShowPhotos(false);
    setCurrentReview(review);
  };

  var onSetShowPhotos = function onSetShowPhotos(showing) {
    setOverlayShowPhotos(showing); // Scroll the all photos view to where they left off.

    setTimeout(function () {
      var scroller = document.querySelector('#all-photos');
      if (!scroller) return;
      scroller.firstChild.scrollTo(0, scrollY.current);
    }, 50);
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SVGIcons, {}), /*#__PURE__*/_jsx(ProductFitRatingReview, {
      colorways: colorways,
      fitRatingAvg: fitRatingAvg,
      ratingAvg: ratingAvg,
      reviewCount: reviewCount,
      reviewWithPhotoCount: reviewWithPhotoCount,
      shopifyId: shopifyId,
      title: title,
      onViewAll: function onViewAll() {
        // console.info('show photos');
        setOverlay(true);
        setOverlayShowPhotos(true);
        setCurrentReview({});
      }
    }), /*#__PURE__*/_jsxs("section", {
      className: css.root,
      children: [(results || isFiltered) && /*#__PURE__*/_jsx("div", {
        className: css.filters,
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          error: error,
          children: /*#__PURE__*/_jsx(CollectionFilters, {
            clearFilters: onClearAll,
            displaySort: true,
            filtered: isFiltered,
            filters: filters.by,
            filterSelect: onFilterSelect,
            itemsLength: reviewCount,
            sort: sort,
            sorts: sorts,
            sortSelect: onSortSelect,
            unpadded: true
          })
        })
      }), fetching && /*#__PURE__*/_jsx("ul", {
        className: css.list,
        children: Array.from(Array(4)).map(function (_, idx) {
          return /*#__PURE__*/_jsx("li", {
            className: css.item,
            children: /*#__PURE__*/_jsx(Loading, {})
          }, idx);
        })
      }), !fetching && results && /*#__PURE__*/_jsx("ul", {
        className: css.list,
        children: reviews.map(function (review) {
          return /*#__PURE__*/_jsx("li", {
            className: css.item,
            id: "review-".concat(toGlobalID(review.id)),
            children: /*#__PURE__*/_jsx(Review, _objectSpread(_objectSpread({}, review), {}, {
              productTitle: title,
              onClickImage: function onClickImage() {
                return _onClickImage(review);
              }
            }))
          }, review.id);
        })
      }), isFiltered && !fetching && !results && /*#__PURE__*/_jsx(ResultsListEmpty, {
        copy: "Looks like there aren\u2019t any reviews with the filters you\u2019ve selected.",
        onClear: onClearAll
      }), /*#__PURE__*/_jsx(Paginator, {
        lastPageIndex: lastPageIndex,
        results: results,
        onClickLast: function onClickLast() {
          return goToIndex(lastPageIndex);
        },
        onClickNext: function onClickNext() {
          return goToIndex(pageIndex + 1);
        },
        onClickPrevious: function onClickPrevious() {
          return goToIndex(pageIndex - 1);
        },
        pageIndex: pageIndex,
        type: "Reviews",
        noResultsMessage: !results && !isFiltered && /*#__PURE__*/_jsx(NoResultsMessage, {
          shopifyId: shopifyId
        }),
        viewAllLink: viewAllLink
      }), currentReview && /*#__PURE__*/_jsx(Overlay, {
        label: "Review",
        on: overlay,
        onHide: closeOverlay,
        children: /*#__PURE__*/_jsx(PhotoModal, {
          id: currentReview.id,
          on: overlay,
          onHide: closeOverlay,
          onSetReview: onSetReview,
          onShowPhotos: onSetShowPhotos,
          orderBy: sort.orderBy,
          productShopifyId: shopifyId,
          showPhotos: overlayShowPhotos,
          title: title
        })
      })]
    })]
  });
};

Reviews.defaultProps = {
  colorways: {
    edges: []
  },
  filterByPhotos: false,
  queryLimit: 25,
  viewAllLink: false
};
Reviews.propTypes = {
  colorways: PropTypes.shape({
    edges: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  filterByPhotos: PropTypes.bool,
  fitRatingAvg: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  queryLimit: PropTypes.number,
  ratingAvg: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  reviewCount: PropTypes.number.isRequired,
  reviewWithPhotoCount: PropTypes.number.isRequired,
  shopifyId: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  viewAllLink: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
};
export default Reviews;